import { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { X, Copy, Check, Share, QrCode } from 'lucide-react';
import { copyToClipboard, nativeShare, showAlert } from '@/lib/capacitor';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';

interface JobReviewQrModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Company's review page, exactly as stored (may be scheme-less). */
    reviewUrl: string;
    companyName: string;
}

/**
 * Full-screen review QR the technician holds up for the customer to scan.
 *
 * Deliberately a stark white sheet rather than the app's usual card-on-scrim: contrast is what
 * actually drives whether a phone camera locks onto the code, so the white background and the
 * black-on-white QR are hardcoded and must survive any future dark theme.
 */
export function JobReviewQrModal({ isOpen, onClose, reviewUrl, companyName }: JobReviewQrModalProps) {
    const [copied, setCopied] = useState(false);
    const layer = useModalLayer({
        enabled: isOpen,
        onClose,
        historyState: { jobReviewQrModal: true },
    });

    // The review link is stored scheme-less on purpose (it is texted verbatim into a
    // 160-character SMS, where "https://" is 8 wasted characters). A QR payload without a
    // scheme decodes as plain *text*, though, so the customer's camera shows them a string
    // instead of opening the browser. Only the encoded value needs the prefix; the URL we
    // print below stays as the owner typed it.
    const qrUrl = /^[a-z][a-z0-9+.-]*:\/\//i.test(reviewUrl) ? reviewUrl : `https://${reviewUrl}`;

    // The real-world failure here is the screen locking while the customer fumbles with their
    // camera, not dimness. No dependency, and unsupported platforms just sleep as before.
    // ponytail: actual brightness control needs a native plugin — deferred, see the plan.
    useEffect(() => {
        if (!isOpen) return;
        let sentinel: { release?: () => Promise<void> } | undefined;
        let released = false;
        const wakeLock = (navigator as Navigator & { wakeLock?: { request: (t: string) => Promise<any> } }).wakeLock;
        wakeLock?.request('screen')
            .then((s) => {
                if (released) { s?.release?.().catch(() => { }); return; }
                sentinel = s;
            })
            .catch(() => { });
        return () => {
            released = true;
            sentinel?.release?.().catch(() => { });
        };
    }, [isOpen]);

    const handleCopy = async () => {
        const success = await copyToClipboard(reviewUrl);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleShare = async () => {
        const result = await nativeShare({
            title: companyName ? `Review ${companyName}` : 'Leave a review',
            url: qrUrl,
        });
        if (result === 'unavailable') {
            const ok = await copyToClipboard(reviewUrl);
            await showAlert(ok ? 'Link copied to clipboard.' : 'Unable to share on this device.', 'Share');
        }
    };

    if (!isOpen) return null;

    return (
        <LayerDepthContext.Provider value={layer.depth}>
            <div className="fixed inset-0 bg-white flex flex-col safe-area-top safe-area-bottom" style={layer.style}>
                {/* Header */}
                <div className="flex items-center justify-between p-4 shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <QrCode className="h-5 w-5 text-gray-400 shrink-0" />
                        <h2 className="text-base font-semibold text-gray-900 truncate">Review QR Code</h2>
                    </div>
                    <button
                        onClick={layer.onClose}
                        aria-label="Close"
                        className="p-2 -mr-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-6 w-6 text-gray-500" />
                    </button>
                </div>

                {/* QR — the part the customer looks at */}
                <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center px-6 text-center">
                    <p className="text-lg font-semibold text-gray-900">Enjoyed our service?</p>
                    <p className="text-sm text-gray-600 mt-1">
                        Scan this code to leave {companyName || 'us'} a review.
                    </p>

                    <div className="my-6 p-4 bg-white">
                        <QRCode
                            value={qrUrl}
                            size={320}
                            bgColor="#FFFFFF"
                            fgColor="#000000"
                            level="M"
                            // Square by construction rather than relying on SVG aspect-ratio
                            // inference, which is unreliable on older WebViews.
                            style={{ width: 'min(68vw, 320px)', height: 'min(68vw, 320px)' }}
                        />
                    </div>

                    <p className="text-xs text-gray-500 break-all select-all max-w-full">{reviewUrl}</p>
                </div>

                {/* Fallbacks for when the camera won't cooperate */}
                <div className="shrink-0 p-4 flex gap-3">
                    <button
                        onClick={handleCopy}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                    >
                        {copied ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
                        {copied ? 'Copied!' : 'Copy link'}
                    </button>
                    <button
                        onClick={handleShare}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                    >
                        <Share className="h-5 w-5" />
                        Share
                    </button>
                </div>
            </div>
        </LayerDepthContext.Provider>
    );
}

export default JobReviewQrModal;
